import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Datepicker from 'components/shared/Datepicker'
import RouteTitle from 'components/shared/RouteTitle'
import { getDateAndDayNameBrackets } from 'helpers/dates'
import { useIntl } from 'react-intl'
import BusHours from './BusHours'
import { OptionProps } from 'components/shared/Select'
import Info from 'components/shared/Info'
import Loader from 'components/shared/Loader'
import { OriginalSettings } from 'routes/Upsell/Bus/fields/BusDatesAndHours'
import { BUS_TICKETS_LIMIT } from 'constants/limits'
import { DeparturePlace } from 'routes/Upsell/Bus/types'

interface BusDateAndHourProps {
    routeIndex?: number
    direction: 'to_park' | 'from_park'
    departurePlace: DeparturePlace
    ticketsQuantity: number
    timeOptions: OptionProps[] | null
    date: string
    onCalendarOpen: () => void
    onDateChange: () => void
    time: string | null
    onTimeChange: (value: string) => void
    onLimitChange: (value: number) => void
    isError: boolean
    isBookingConflict: boolean
    errorMsg: string | null
    isFetching: boolean
    originalSettings?: OriginalSettings
    isRequiresPurchaseTimeSlot?: boolean
    testIdPrefix?: string
    hasMixedTimeSlotsError?: boolean
    hasWrongDateError?: boolean
    isInRange?: boolean
}

export const BusDateAndHour = ({
    routeIndex,
    direction,
    departurePlace,
    ticketsQuantity,
    timeOptions,
    date,
    onCalendarOpen,
    onDateChange,
    time,
    onTimeChange,
    onLimitChange,
    isError,
    isBookingConflict,
    errorMsg,
    isFetching,
    originalSettings,
    isRequiresPurchaseTimeSlot,
    testIdPrefix,
    hasMixedTimeSlotsError,
    hasWrongDateError,
    isInRange,
}: BusDateAndHourProps) => {
    const { formatMessage } = useIntl()

    const isNoTransport = !isFetching && timeOptions === null

    const isNoTickets =
        !isNoTransport &&
        !!timeOptions &&
        timeOptions.every((option) => option.isDisabled === true) &&
        !originalSettings

    const timeOptionsCurrentMaxSeats = (() => {
        let selectedTimeMaxSeats = 0

        const freeSeatsNumbers = timeOptions?.map((option) => {
            if (option.isDisabled || !option.optionInfo) return 0

            let freeSeatsNum = option.optionData || 0

            if (originalSettings) {
                const { hour, quantity } = originalSettings
                const isActuallySelectedOption = option.option === hour
                freeSeatsNum += isActuallySelectedOption ? quantity : 0
            }

            if (time === option.option) selectedTimeMaxSeats = freeSeatsNum
            return freeSeatsNum
        }) || [BUS_TICKETS_LIMIT]

        return selectedTimeMaxSeats || Math.max(...freeSeatsNumbers)
    })()

    const isNotEnoughSeats =
        !isNoTransport &&
        !!timeOptions &&
        !!timeOptions.length &&
        timeOptions[0].option !== '' &&
        ticketsQuantity > timeOptionsCurrentMaxSeats

    const getRouteTitle = (title: DeparturePlace) => {
        const translationKeyData = {
            id: `upsells.transport.direction_${title}`,
            defaultMessage: '_',
        }
        return formatMessage(translationKeyData)
    }

    const NoTransportMsg = (
        <Info color="error">
            {formatMessage({
                id: 'upsells.transport.msg_no_transport',
                defaultMessage:
                    'Brak rozkładu jazdy na wybrany dzień. Wybierz inną datę.',
            })}
        </Info>
    )

    const NotEnoughSeatsMsg = (
        <Info color="info">
            {formatMessage({
                id: 'upsells.transport.msg_not_enough_seats',
                defaultMessage:
                    'Za mało miejsc? Możecie się podzielić i kupić bilety także na najbliższą wolną godzinę.',
            })}
        </Info>
    )

    const WrongDatesCombinationMsg = (
        <Info color="error">
            {formatMessage({
                id: 'upsells.transport.msg_wrong_dates_combination',
                defaultMessage:
                    'Nie można wybrać wskazanej daty powrotu przy podanej dacie przyjazdu. Wybierz inną datę powrotu lub zmień datę przyjazdu.',
            })}
        </Info>
    )

    const WrongDateMsg = (
        <Info color="error">
            {formatMessage({
                id: 'upsells.transport.msg_wrong_date',
                defaultMessage:
                    'Nie można zapisać wskazanej daty. Wybierz inną datę przejazdu.',
            })}
        </Info>
    )

    const ErrorMsg = (msg: string) => <Info color="error">{msg}</Info>

    return (
        <Box gap={12} isFullWidth>
            <RouteTitle
                from={getRouteTitle(
                    direction === 'to_park' ? departurePlace : 'suntago'
                )}
                to={getRouteTitle(
                    direction === 'from_park' ? departurePlace : 'suntago'
                )}
            />

            {!!date && (
                <Text as="span" variant="body" color="grayscale_700">
                    {getDateAndDayNameBrackets(date)}
                    <Text
                        as="button"
                        variant="bodyStrong"
                        color="secondary_500"
                        ml={8}
                        onClick={onDateChange}
                        data-testid={`${
                            testIdPrefix ?? 'transport'
                        }-${routeIndex}-button-change-date-${direction}`}
                    >
                        {formatMessage({
                            id: 'global.btn.change',
                            defaultMessage: 'Zmień',
                        })}
                    </Text>
                </Text>
            )}

            {isFetching ? (
                <Loader isFullWidth align="flex-start" />
            ) : (
                <>
                    {!date && (
                        <Datepicker
                            id={
                                direction === 'to_park'
                                    ? 'departureDate'
                                    : 'returnDate'
                            }
                            value={date ? [date] : []}
                            openCalendar={onCalendarOpen}
                            placeholder={formatMessage({
                                id: 'global.input.choose_date_label',
                                defaultMessage: 'Wybierz datę',
                            })}
                            testId={`${
                                testIdPrefix ?? 'transport'
                            }-${routeIndex}-trigger-date-${direction}`}
                        />
                    )}

                    {!hasMixedTimeSlotsError &&
                        isRequiresPurchaseTimeSlot &&
                        !!date &&
                        timeOptions &&
                        !isError &&
                        isInRange && (
                            <Box
                                direction={'column'}
                                gap={16}
                                align={['flex-start', 'center']}
                                mt={16}
                                isFullWidth
                            >
                                {isNoTickets ||
                                    (isNotEnoughSeats && NotEnoughSeatsMsg)}

                                {isBookingConflict &&
                                    !!errorMsg &&
                                    ErrorMsg(errorMsg)}

                                <BusHours
                                    options={timeOptions}
                                    hour={time || ''}
                                    setHour={onTimeChange}
                                    setSeatsLimit={onLimitChange}
                                    id={
                                        direction === 'to_park'
                                            ? 'departureTime'
                                            : 'returnTime'
                                    }
                                    isNoTickets={isNoTickets}
                                    selectedTicketsAmount={ticketsQuantity}
                                    originalSettings={originalSettings}
                                    routeIndex={routeIndex}
                                    testIdPrefix={testIdPrefix}
                                />
                            </Box>
                        )}

                    {(isNoTransport || isInRange === false) &&
                        !isError &&
                        !!date &&
                        NoTransportMsg}

                    {isError &&
                        (errorMsg ? ErrorMsg(errorMsg) : NoTransportMsg)}

                    {!hasMixedTimeSlotsError &&
                        hasWrongDateError &&
                        WrongDateMsg}

                    {hasMixedTimeSlotsError && WrongDatesCombinationMsg}
                </>
            )}
        </Box>
    )
}
